/**
 * The words of the trade, explained so anybody gets them at first read. `<Term k="…">` shows one in a bubble and the
 * help panel lists the ones each screen uses.
 */
export interface GlossaryEntry {
  word: string;
  text: string;
  example?: string;
}

export const GLOSSARY = {
  consulta: {
    word: 'Consulta',
    text: 'Cuando alguien nos pregunta por un trabajo (por WhatsApp, teléfono o en persona). Es el primer paso, antes del presupuesto.',
  },
  presupuesto: {
    word: 'Presupuesto',
    text: 'El papel con las piezas, los materiales y el precio que le mandamos al cliente para que diga que sí.',
  },
  borrador: {
    word: 'Borrador',
    text: 'Un presupuesto que todavía estás armando. El cliente no lo ve hasta que lo terminás.',
  },
  enviar: {
    word: 'Terminar y generar el PDF',
    text: 'Deja el presupuesto listo y fijo, con su PDF para mandarle al cliente. Si después hay que cambiar algo, se hace una versión nueva.',
  },
  version: {
    word: 'Versión',
    text: 'Cada vez que cambiás un presupuesto ya enviado se crea una versión nueva. Las anteriores quedan guardadas para ver qué cambió.',
    example: 'Versión 1: mesada de 2 m. Versión 2: el cliente pidió 2,20 m.',
  },
  titular: {
    word: 'A nombre de quién',
    text: 'La persona o empresa nuestra que firma el presupuesto y cobra. Sale impresa en el PDF.',
  },
  cliente: { word: 'Cliente', text: 'La persona o empresa que nos compra.' },
  obra: {
    word: 'Obra',
    text: 'El lugar donde va el trabajo (una casa, un local). Un cliente puede tener varias obras. De la dirección sale el costo del viaje.',
  },
  pieza: {
    word: 'Pieza',
    text: 'Cada parte que se corta: una mesada, un zócalo, un umbral. Se carga con su largo, ancho y material.',
    example: 'Mesada de cocina: 200 × 60 cm, granito gris mara.',
  },
  adicionales: {
    word: 'Adicionales',
    text: 'Lo que se suma además de la piedra: colocación, viaje, bachas, agujeros para la grifería.',
  },
  condiciones: {
    word: 'Condiciones',
    text: 'Las reglas del presupuesto: hasta cuándo vale, en qué moneda y cuánto hay que dejar de seña.',
  },
  vigencia: {
    word: 'Válido hasta',
    text: 'El último día en que el precio del presupuesto se respeta. Después hay que hacer una versión nueva.',
  },
  aceptado: {
    word: 'Aceptado',
    text: 'El cliente dijo que sí. Ahí el presupuesto se convierte en un trabajo para hacer.',
  },
  trabajo: {
    word: 'Trabajo',
    text: 'Un presupuesto que el cliente aceptó. Acá seguís todo: medir, cortar, entregar y cobrar.',
  },
  etapa: {
    word: 'Etapa',
    text: 'En qué parte del camino está el trabajo: medición, compras, corte, lijado, pulido, listo, colocación y entregado.',
  },
  sena: {
    word: 'Seña',
    text: 'Un adelanto que paga el cliente para que empecemos a comprar el material y a cortar.',
    example: 'Trabajo de $ 1.000.000 con 50 % de seña: el cliente paga $ 500.000 para arrancar.',
  },
  saldo: {
    word: 'Falta cobrar',
    text: 'Lo que el cliente todavía nos debe. Cuando llega a cero, el trabajo está pagado.',
  },
  saldoAFavor: {
    word: 'Saldo a favor',
    text: 'Plata que el cliente pagó de más y que le tenemos que devolver.',
  },
  cobro: {
    word: 'Cobro',
    text: 'Cada pago que nos hace el cliente (efectivo, transferencia, cheque). Queda anotado en el trabajo y en la caja.',
  },
  pagoAvisado: {
    word: 'Pago por confirmar',
    text: 'Alguien avisó que el cliente pagó, pero todavía no vimos la plata en la cuenta. Cuando la vemos, se confirma.',
  },
  deshacer: {
    word: 'Deshacer',
    text: 'Cancela un pago o un gasto cargado por error. No se borra: queda en el historial que se deshizo, para que siempre se pueda ver qué pasó.',
  },
  darDeBaja: {
    word: 'Dar de baja',
    text: 'Dejar sin efecto algo que se cargó. Sigue apareciendo tachado en el historial.',
  },
  entrega: {
    word: 'Entrega',
    text: 'Cuando las piezas llegan al cliente. Puede ser de a partes o todo junto, y queda un papel firmado.',
  },
  costos: {
    word: 'Gastos del trabajo',
    text: 'Todo lo que nos costó hacerlo: la placa, la mano de obra, el viaje. Con eso sabemos cuánto ganamos.',
  },
  ganancia: {
    word: 'Ganancia',
    text: 'Lo que nos queda: lo que cobramos menos lo que gastamos. El margen es esa ganancia en porcentaje.',
    example: 'Cobramos $ 100 y gastamos $ 70: ganamos $ 30, un margen del 30 %.',
  },
  placa: {
    word: 'Placa',
    text: 'Una plancha entera de piedra (mármol, granito) tal como la trae el proveedor. De ahí se cortan las piezas.',
  },
  sobrante: {
    word: 'Sobrante',
    text: 'El pedazo de placa que queda después de cortar. Se guarda para otro trabajo o para hacer productos.',
  },
  merma: {
    word: 'Lo que se perdió',
    text: 'La parte de la placa que no sirve más después de cortar (polvo, recortes chicos, una rotura).',
  },
  reservar: {
    word: 'Reservar',
    text: 'Apartar una placa o un sobrante para un trabajo, así nadie más la usa.',
  },
  proveedor: {
    word: 'Proveedor',
    text: 'Quien nos vende la piedra, las bachas u otros materiales.',
  },
  comprobante: {
    word: 'Factura de proveedor',
    text: 'La factura, el remito o el presupuesto que nos manda un proveedor. Al confirmarla queda anotado lo que le debemos y lo que costó.',
  },
  deuda: {
    word: 'Lo que le debemos',
    text: 'Plata que todavía tenemos que pagarle a un proveedor por algo que compramos.',
  },
  caja: {
    word: 'Caja',
    text: 'Todo lo que entró y salió de nuestras cuentas: cobros a clientes, devoluciones y pagos a proveedores.',
  },
  cuenta: {
    word: 'Cuenta',
    text: 'Dónde está guardada nuestra plata: una cuenta del banco, Mercado Pago o la caja de efectivo del taller.',
  },
  dolar: {
    word: 'Precio del dólar',
    text: 'Usamos el dólar oficial (venta) para pasar precios de dólares a pesos. Se actualiza solo desde la página de Ámbito.',
  },
  catalogo: {
    word: 'Lista de precios',
    text: 'Los materiales y servicios que vendemos, con su precio. El presupuesto toma los precios de acá.',
  },
  cuit: {
    word: 'CUIT',
    text: 'El número que identifica a una persona o empresa ante la AFIP. Viene impreso en las facturas.',
  },
  razonSocial: {
    word: 'Razón social',
    text: 'El nombre legal de una empresa, el que figura en sus facturas.',
  },
  iva: {
    word: 'IVA',
    text: 'Un impuesto que se suma al precio. Hay que indicar si el precio que ves ya lo tiene incluido o no.',
  },
  visita: {
    word: 'Visita',
    text: 'Cuando vamos a la obra: a medir, a entregar o a colocar. Se programa en la agenda.',
  },
} satisfies Record<string, GlossaryEntry>;

export type GlossaryKey = keyof typeof GLOSSARY;

export function glossary(k: GlossaryKey): GlossaryEntry {
  return GLOSSARY[k];
}
